<template>
  <div class="question-info">
    <div v-if="questionInfo.id !== -1" class="container">
      <div class="main-info">
        <h2 :aria-label="questionInfo.text" class="title">{{ questionInfo.text }}</h2>
        <read-more-text class="instructions" :text="questionInfo.instructions"></read-more-text>
        <div class="additional-info">
          <span>{{ t('MAX. DURATION') }} {{ readableMaxDuration }}</span>
          <span>{{ t('ORIENTATION') }} {{ questionInfo.orientation }}</span>
        </div>
      </div>
    </div>
    <div v-else class="answer-list__row">
      <div>
        <span class="skeleton-box avatar"></span>
      </div>
      <div class="answer-list__row-body">
        <span class="answer-list__headline">
          <span class="skeleton-box" style="width: 75%"></span>
        </span>
        <span class="answer-list__info">
          <span class="skeleton-box" style="width: 50%"></span>
        </span>
        <span class="answer-list__info tags">
          <span class="skeleton-box" style="width: 20%"></span>
          <span class="skeleton-box" style="width: 20%"></span>
        </span>
      </div>
    </div>
    <font-awesome-icon :icon="['far', 'question-circle']" />
  </div>
</template>

<script lang="ts">
import { Question } from 'altru-types';
import secondsToReadableTime from '@/utils/secondsToReadableTime';
import { useStore } from 'vuex';
import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ReadMoreText from '@/components/ReadMoreText.vue';

export default {
  name: 'QuestionInfo',
  components: { ReadMoreText },
  setup(): { questionInfo: Ref<Question>; readableMaxDuration: Ref<string>; t: (key: string) => string } {
    const { t } = useI18n();
    const store = useStore();

    const questionInfo: Ref<Question> = computed(() => store.getters['questions/questionInfo']);
    const readableMaxDuration: Ref<string> = computed(() =>
      questionInfo.value.max_duration ? secondsToReadableTime(questionInfo.value.max_duration) : '-',
    );

    return { questionInfo, readableMaxDuration, t };
  },
};
</script>

<style lang="scss" scoped>
.question-info {
  border-radius: 0.5rem;
  border: 1px solid $primary;
  overflow: hidden;
  padding: 14px;
  margin: 0 auto;
  position: relative;

  @media (min-width: $tablet) {
    min-width: 320px;
  }

  .container {
    display: flex;
    flex-wrap: wrap;

    @media (min-width: $mobile) {
      flex-wrap: nowrap;
    }

    .main-info {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .instructions {
        display: block;
        font-size: 14px;
        white-space: pre-wrap;
      }

      .title {
        font-size: 18px;
        margin: 0;
        word-break: break-word;
        line-height: 24px;
      }

      .additional-info {
        display: flex;

        span {
          color: rgba(0, 0, 0, 0.5);
          font-size: 0.8rem;
          font-weight: bold;
        }

        > * + * {
          margin-left: 1rem;
        }
      }
    }
  }

  svg {
    position: absolute;
    top: 50%;
    right: 1rem;
    height: 10rem;
    width: 10rem;
    opacity: 0.025;
    transform: translateY(-50%) rotate(15deg);
    z-index: -1;
  }
}

.answer-list__row {
  display: flex;

  .answer-list__headline {
  }

  .answer-list__info {
    display: block;
    margin-top: 0.5rem;

    &.tags {
      display: block;

      > * + * {
        margin-left: 0.5rem;
      }
    }
  }

  .skeleton-box {
    display: inline-block;
    height: 0.75rem;
    position: relative;
    overflow: hidden;
    background-color: #eaeaea;

    &.avatar {
      border-radius: 0.25rem;
      height: 70px;
      width: 60px;
    }

    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transform: translateX(-100%);
      background-image: linear-gradient(
        90deg,
        rgba(#fff, 0) 0,
        rgba(#fff, 0.2) 20%,
        rgba(#fff, 0.5) 60%,
        rgba(#fff, 0)
      );
      animation: shimmer 2s infinite;
      content: '';
    }

    @keyframes shimmer {
      100% {
        transform: translateX(100%);
      }
    }
  }

  .answer-list__row-body {
    flex-grow: 1;
    margin-left: 1em;
  }
}
</style>
